import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PremiumTable from '../components/common/PremiumTable';
import PageHeader from '../components/common/PageHeader';
import { EyeIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { generateTaxInvoice } from '../utils/generateInvoice';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getAllInvoices();
      setInvoices(res.data.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    if (!status) return null;
    const stat = status.toLowerCase();
    const colors = {
      paid: 'bg-green-100 text-green-800 border-green-200',
      partially_paid: 'bg-teal-100 text-teal-800 border-teal-200',
      overdue: 'bg-red-100 text-red-800 border-red-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      cancelled: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return (
      <span className={`px-3 py-1 inline-flex text-xs font-bold rounded-full border ${colors[stat] || 'bg-gray-100'}`}>
        {status.toUpperCase().replace('_', ' ')}
      </span>
    );
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const includeGst = invoice.taxAmount > 0;
      const gstRate = 18; // Default GST rate if tax is applied
      
      const customerMock = invoice.customerId || invoice.walkInCustomer || {};
      
      await generateTaxInvoice(invoice, invoice.businessId, customerMock, includeGst, gstRate);
      
      toast.success('Invoice Downloaded successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    }
  };

  const columns = [
    {
      header: 'Invoice No',
      accessor: 'invoiceNumber',
      render: (row) => (
        <span className="text-sm font-bold text-gray-900">
          #{row.invoiceNumber}
        </span>
      )
    },
    {
      header: 'Business & Customer',
      accessor: 'businessId.name',
      render: (row) => {
        const customerName = row.customerId?.name || row.walkInCustomer?.name || 'Unknown';
        return (
          <div>
            <div className="text-sm font-bold text-gray-900">B: {row.businessId?.name || 'Unknown'}</div>
            <div className="text-xs font-medium text-gray-500">C: {customerName}</div>
          </div>
        );
      }
    },
    {
      header: 'Amount',
      accessor: 'totalAmount',
      render: (row) => (
        <span className="text-sm font-extrabold text-gray-900">₹{row.totalAmount || 0}</span>
      )
    },
    {
      header: 'Due Date',
      accessor: 'dueDate',
      render: (row) => (
        <span className="text-sm font-medium text-gray-500">
          {row.dueDate ? new Date(row.dueDate).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (row) => statusBadge(row.status)
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedInvoice(row);
              setIsModalOpen(true);
            }}
            className="p-1.5 bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors"
            title="View Invoice"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDownloadPDF(row)}
            className="p-1.5 bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-600 rounded-md transition-colors"
            title="Download PDF"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title="Invoice Management"
        subtitle="View, track and download generated invoices seamlessly."
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading invoices...</div>
        ) : (
          <PremiumTable 
            title="Global Invoices"
            data={invoices}
            columns={columns}
            searchKeys={['invoiceNumber', 'businessId.name', 'customerId.name', 'status']}
            primaryKey="_id"
            paginationMode="standard"
            itemsPerPage={10}
          />
        )}
      </main>

      {/* View Invoice Modal */}
      {isModalOpen && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Invoice #{selectedInvoice.invoiceNumber}</h3>
                <p className="text-xs text-gray-500">Created: {new Date(selectedInvoice.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-4">
                {statusBadge(selectedInvoice.status)}
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">From (Business)</h4>
                  <p className="text-base font-bold text-gray-900">{selectedInvoice.businessId?.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-600">{selectedInvoice.businessId?.email}</p>
                </div>
                <div className="text-right">
                  <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">To (Customer)</h4>
                  <p className="text-base font-bold text-gray-900">
                    {selectedInvoice.customerId?.name || selectedInvoice.walkInCustomer?.name || 'Walk-in Customer'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedInvoice.customerId?.phone || selectedInvoice.walkInCustomer?.phone || ''}
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Item</th>
                      <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Qty</th>
                      <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Price</th>
                      <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedInvoice.items?.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-right">{item.quantity}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-right">₹{item.unitPrice}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">₹{item.total}</td>
                      </tr>
                    ))}
                    {(!selectedInvoice.items || selectedInvoice.items.length === 0) && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-sm text-gray-500 text-center">No items found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <div className="w-64 space-y-3">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-medium text-gray-900">₹{selectedInvoice.subtotal || 0}</span>
                  </div>
                  {selectedInvoice.taxAmount > 0 && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax</span>
                      <span className="font-medium text-gray-900">₹{selectedInvoice.taxAmount}</span>
                    </div>
                  )}
                  {selectedInvoice.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Discount</span>
                      <span className="font-medium text-green-600">-₹{selectedInvoice.discountAmount}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900">Total Amount</span>
                    <span className="text-xl font-extrabold text-blue-600">₹{selectedInvoice.totalAmount || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => handleDownloadPDF(selectedInvoice)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download PDF
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
